import { Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import {
  HSpacer,
  RoutalDialog,
  RoutalLoader,
  RoutalPalette,
  RoutalTab,
  RoutalTabs,
  RoutalWarningIcon,
  VSpacer,
} from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import AddTask from '../../assets/svg/AddTask';
import { default as ClientIcon } from '../../assets/svg/custom-field-models/Client';
import { Client } from '../../domain/client/Client';
import { Stop } from '../../domain/stop/Stop';
import { intl } from '../../helpers/IntGlobalProvider';
import { useRetrieveActiveProject } from '../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectResources } from '../../hooks/project/useRetrieveActiveProjectResources';
import { useCreateStops } from '../../hooks/stop/useCreateStops';
import Button, { ButtonProps } from '../../react-components/Button';
import Text from '../../react-components/Text';
import eventsPanel from '../../services/events';
import { StopsFromClients } from './StopsFromClients';
import { StopsFromScratch } from './StopsFromScratch';

interface CreateStopsManuallyDialogProps {
  open: boolean;
  planStops?: Stop[];
  planId?: string;
  initialLocation?: Location;
  handleCloseDialog: (e: React.MouseEvent<ButtonProps, MouseEvent>) => void;
}

type ScratchStopData = {
  label: string;
  location?: Location;
};

type TabsValue = `from_scratch` | `from_clients`;

export const CreateStopsManuallyDialog = ({
  open,
  planStops = [],
  planId,
  initialLocation,
  handleCloseDialog,
}: CreateStopsManuallyDialogProps) => {
  const { run: createStops, success, isLoading } = useCreateStops({ planId });
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: projectResources } = useRetrieveActiveProjectResources();
  const [scratchStops, setScratchStops] = useState<ScratchStopData[]>([]);
  const [clientStops, setClientStops] = useState<Client[]>([]);
  const [createButtonEvent, setCreateButtonEvent] = useState<React.MouseEvent<ButtonProps, MouseEvent> | null>(null);

  const [value, setValue] = useState<TabsValue>(`from_scratch`);
  const [actionHover, setActionHover] = useState<Record<TabsValue, boolean>>({
    from_scratch: false,
    from_clients: false,
  });

  useEffect(() => {
    if (open) {
      setClientStops([]);
      setScratchStops([]);
      setCreateButtonEvent(null);
    }
  }, [open]);

  useEffect(() => {
    if (success) {
      eventsPanel.createStops(`Manual`);
      handleCloseDialog(createButtonEvent!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  useEffect(() => {
    if (initialLocation) {
      setScratchStops([{ label: ``, location: initialLocation }]);
    }
    return () => {
      setScratchStops([]);
    };
  }, [initialLocation, open]);

  const maxStopsPerPlan = projectResources?.max_stops_plan ?? 0;

  const getTotalNewStops = () => {
    const validScratchStops = scratchStops.filter((stop: any) => (stop.label ?? ``) !== ``);
    const totalNewStops = validScratchStops.length + clientStops.length;

    return totalNewStops;
  };

  const remainingQuota = () => {
    return maxStopsPerPlan - ((planStops?.length ?? 0) + getTotalNewStops());
  };

  const exceedsQuota = () => {
    if (!planId) {
      return false;
    }
    return remainingQuota() < 0;
  };

  const canCreateStops = () => {
    return !isLoading && !exceedsQuota() && getTotalNewStops() > 0;
  };

  const handleCreateStops = (e: React.MouseEvent<ButtonProps, MouseEvent>) => {
    const cleanScratchStops = scratchStops.map((stop: ScratchStopData) => {
      const { label, location } = stop;
      return Stop.create({
        type: `delivery`,
        status: `pending`,
        ...(label && label !== `` ? { label: label } : {}),
        location,
        project_id: activeProject!.id,
        supervisors: [],
      });
    });

    const cleanClientStops = clientStops.map((client: Client) => client.stopFromClient());

    setCreateButtonEvent(e);
    createStops({ stops: [...cleanScratchStops, ...cleanClientStops] });
  };

  return (
    <RoutalDialog
      open={open}
      dialogProps={{
        maxWidth: `md`,
        dialogBGColor: `white`,
        disableBackdropClick: isLoading,
        disableEscapeKeyDown: isLoading,
      }}
      onCloseDialog={handleCloseDialog}
      titleContent={<Text variant="h6" intlId="plans.dialogs.create_stops_manually.title" />}
      contentStyles={{
        height: `400px`,
        paddingTop: 0,
        padding: `0px 20px 20px 20px`,
      }}
      showCrossCloseButton={!isLoading}
      footerContent={
        <Grid container justifyContent="space-between" alignItems="center" wrap="nowrap">
          <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `unset` }}>
            <Grid
              item
              container
              direction="row"
              alignItems="center"
              justifyContent="center"
              wrap="nowrap"
              style={{
                minWidth: `20px`,
                width: `unset`,
                padding: `2px 6px`,
                borderRadius: `5px`,
                backgroundColor: RoutalPalette.primary40,
                color: RoutalPalette.neutral00,
              }}
            >
              {getTotalNewStops()}
            </Grid>
            <HSpacer small />
            <Grid item>
              <Text
                intlId="plans.dialogs.create_stops_manually.footer.added_stops"
                style={{ color: RoutalPalette.primary40 }}
              />
            </Grid>
          </Grid>
          {planId && remainingQuota() < 0 && (
            <Grid
              item
              container
              direction="row"
              alignItems="center"
              wrap="nowrap"
              style={{ width: `unset`, maxWidth: `450px` }}
            >
              <RoutalWarningIcon style={{ color: RoutalPalette.error.medium }} />
              <HSpacer small />
              <Text
                intlId="plans.servicesdialog.addservices.quota_exceeded"
                intlValues={{
                  maxServices: maxStopsPerPlan,
                  surplus: -remainingQuota(),
                }}
                style={{ color: RoutalPalette.error.medium }}
              />
            </Grid>
          )}
          <Grid item>
            <Button
              style={{ minWidth: `120px` }}
              disabled={!canCreateStops()}
              intlId="plans.dialogs.create_stops_manually.footer.add_stops"
              onClick={(e) => handleCreateStops(e)}
            />
          </Grid>
        </Grid>
      }
    >
      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{
          height: `100%`,
        }}
      >
        <Grid style={{ width: `100%`, height: `44px` }}>
          <RoutalTabs
            value={value}
            onChange={(_event: React.SyntheticEvent, newValue: string) => {
              if (!isLoading) {
                setValue(newValue as TabsValue);
              }
            }}
          >
            <RoutalTab
              value={`from_scratch`}
              iconPosition="start"
              icon={
                <Grid style={{ display: `flex`, flexDirection: `row` }}>
                  <AddTask
                    color={
                      actionHover[`from_scratch`]
                        ? RoutalPalette.primary20
                        : value === `from_scratch`
                        ? RoutalPalette.primary40
                        : RoutalPalette.secondary20
                    }
                  />
                </Grid>
              }
              setHover={(newHover) => {
                setActionHover({ ...actionHover, from_scratch: newHover });
              }}
              label={intl.formatMessage({ id: `plans.dialogs.create_stops_manually.from_scratch` })}
              style={{ height: `44px`, padding: `0px` }}
            />
            <RoutalTab
              value={`from_clients`}
              iconPosition="start"
              icon={
                <Grid style={{ display: `flex`, flexDirection: `row` }}>
                  <ClientIcon
                    color={
                      actionHover[`from_clients`]
                        ? RoutalPalette.primary20
                        : value === `from_clients`
                        ? RoutalPalette.primary40
                        : RoutalPalette.secondary20
                    }
                  />
                </Grid>
              }
              setHover={(newHover) => {
                setActionHover({ ...actionHover, from_clients: newHover });
              }}
              label={intl.formatMessage({ id: `plans.dialogs.create_stops_manually.from_clients` })}
              style={{ height: `44px`, padding: `0px` }}
            />
          </RoutalTabs>
        </Grid>

        <VSpacer large />

        {isLoading ? (
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            wrap="nowrap"
            style={{ height: `100%` }}
          >
            <Grid item>
              <RoutalLoader />
            </Grid>
            <VSpacer small />
            <Text variant="label" intlId={`plans.dialogs.create_stops_manually.loading`} />
          </Grid>
        ) : (
          <>
            {value === `from_scratch` ? (
              <StopsFromScratch stops={scratchStops} setStops={setScratchStops} />
            ) : (
              <StopsFromClients clientStops={clientStops} setClientStops={setClientStops} />
            )}
          </>
        )}
      </Grid>
    </RoutalDialog>
  );
};
